import React, { useContext, useState, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { App, Button, Dropdown } from 'antd';

import RecordBadge, { RecordIdentity } from '../common/RecordBadge';
import { buildActionMenu } from '../common/actionMenu';
import { addExampleData, addExampleDataLabel, exampleDataAddedNote } from '../../services/packages';
import RemoveDemoDataModal from './RemoveDemoDataModal';
import { useHelpAction } from '../../contexts/ShortcutsContext';
import { useOptionalFavorites } from '../../contexts/FavoritesContext';
import { PIN_KIND } from '../../services/favorites';
import ExportModal from '../common/ExportModal';
import AddItemButton from '../items/AddItemButton';
import { useItemsViewShare } from '../items/ItemsViewShareButton';
import ShareModal from '../common/ShareModal';
import TempShareLinkSection from '../common/TempShareLinkSection';
import { buildCollectionShareUrl } from '../../utils/shareUrls';
import { canAddItemsNow, canCreateItems, canManage, canPinCollection, canTagCollectionPrivately, isGlobalConsumer, isOwner } from '../../utils/permissions';
import { isGlobal, isPersonal } from '../../utils/collectionKinds';
import { ShellSlotContext } from '../shell/ShellSlot';
import { useCollectionContext } from '../../contexts/CollectionContext';
import { followCollection, publicationRefusal, unfollowCollection } from '../../services/publication';
import { readItemsView, resolveItemsViewMode } from '../../utils/itemsView';
import { DEFAULT_GROUP_TOKEN } from '../../utils/itemGroups';
import { resolveItemsGroup } from '../../utils/itemsGroup';
import { IMPORT_LABEL, importPath } from '../../utils/importEntry';
import { slugify } from '../../utils/strings';
import { BOOKMARK, KEBAB, PLUS, RATING_STAR_FILL, RATING_STAR_OUTLINE, UPLOAD } from '../icons';
import useLinkedSources from '../../hooks/useLinkedSources';
import { canSeeLinks } from '../../utils/collectionLinks';
import { resolveLinkedParam } from '../../utils/linkedRecords';
import { canAddSubCollection, canAttachSubCollection } from '../../utils/familyActions';
import NewSubCollectionModal from './NewSubCollectionModal';
import AttachSubCollectionModal from './AttachSubCollectionModal';
import CollectionTagsModal from '../tags/CollectionTagsModal';
import useRecordCodesVersion from '../../hooks/useRecordCodes';
import { useOptionalCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { getCollectionFamilyCardIds } from '../../utils/collections';
import { cardsHoldRepeatingGroup } from '../../utils/groupColumns';
import { normalizeId } from '../../utils/ids';
import { sortThenParams } from '../../utils/sortLevels';

// Exported so surfaces that place the favorite toggle on its own (the
// collection tiles' hover controls) can reuse it.
export function CollectionFavoriteButton({
	buttonSize = 'middle',
	buttonShape = 'default',
	className = 'collection-actions__button',
	disabled = false,
	isFavorite = false,
	onToggleFavorite,
}) {
	if (typeof onToggleFavorite !== 'function') return null;
	const FavoriteIcon = isFavorite ? RATING_STAR_FILL : RATING_STAR_OUTLINE;
	const label = isFavorite ? 'Remove from favorites' : 'Add to favorites';
	return (
		<Button
			type="text"
			shape={buttonShape}
			size={buttonSize}
			aria-label={label}
			title={label}
			icon={<FavoriteIcon className="medium-icon" />}
			disabled={disabled}
			className={className}
			onClick={onToggleFavorite}
		/>
	);
}

/**
 * The collection's own header actions, in the platform action grammar:
 * everything but the sections behind a single ⋯ menu built from the action
 * taxonomy (navigate · record · share · destructive), and one filled primary
 * ("Add item") anchoring the right edge. The collection's sections (Home ·
 * List · Reports · Settings) are not here: they are the shell header's tabs
 * (components/shell/ShellHeader.js, config/collectionShell.js), which hide
 * Reports and Settings by the same grants the pages enforce.
 */
export default function CollectionActions({
	collection,
	collectionId,
	className = '',
	isFavorite = false,
	onToggleFavorite,
	showPrimary = true,
}) {
	const navigate = useNavigate();
	const location = useLocation();
	const [searchParams] = useSearchParams();
	const [shareOpen, setShareOpen] = useState(false);
	const [exportOpen, setExportOpen] = useState(false);
	const [following, setFollowing] = useState(false);
	// Sub-collections (docs/plans/sub-collections.md §5.1): New
	// sub-collection… (managers of an ordinary top-level collection) and
	// Attach an existing collection… (its owner), in the Record group.
	const [newSubOpen, setNewSubOpen] = useState(false);
	const [attachOpen, setAttachOpen] = useState(false);
	// Tag… (tags b53): the person's private tags on this collection.
	const [tagOpen, setTagOpen] = useState(false);
	const [demoOpen, setDemoOpen] = useState(false);
	const { message, modal } = App.useApp();
	const refreshCollection = useCollectionContext()?.refreshCollection;

	const resolvedId = collectionId || collection?._id || '';

	// Add example data (keepr-api KPR-365): a package installed without its
	// examples brings them in later, on its own cards, marked Demo. The API
	// lists, for managers alone and never on an archived collection, each
	// package here with examples still to add (`exampleData`); one entry each.
	const exampleData = Array.isArray(collection?.exampleData) ? collection.exampleData.filter(entry => entry?.key && Number(entry.items) > 0) : [];
	const confirmAddExamples = entry => modal.confirm({
		title: `Add example data from ${entry.name || 'this package'}?`,
		content: `${entry.items === 1 ? 'One example record lands' : `${entry.items} example records land`} on the package's cards here, marked Demo. Remove demo data takes them out again.`,
		okText: 'Add examples',
		onOk: async () => {
			try {
				const answer = await addExampleData(resolvedId, entry.key);
				message.success(exampleDataAddedNote(answer));
				const said = Array.isArray(answer?.warnings) ? answer.warnings.find(w => w?.message) : null;
				if (said) message.warning(said.message);
				if (typeof refreshCollection === 'function') refreshCollection();
			} catch (e) {
				message.error(e?.response?.data?.message || 'The example data could not be added.');
			}
		},
	});

	// "Share this view" belongs to the items list: it snapshots the filter
	// and sort straight from the URL, which is that page's source of truth.
	const onItemsRoute = /\/items\/?$/.test(location.pathname);
	// The grid's separator period travels with the link the way the sort
	// does. It is read the way the page reads it (?group=, else the stored
	// per-collection seed), and the `day` default is left out — exactly as
	// ?group= leaves it out of the URL — so a reader's own period survives.
	const groupBy = resolveItemsGroup(searchParams, resolvedId);
	// The page's own view, read with the page's own reader (utils/itemsView,
	// KPR-1579): the review allowlist, the trimmed query, `linked` raw. A sort
	// travels only when the address names one, and as the page reads it — a
	// hand-mangled ?dir= is the page's newest-first, never sent on as typed.
	const view = useMemo(() => readItemsView(searchParams), [searchParams]);
	const { review } = view;
	const sortFieldNamed = searchParams.has('sort') ? view.sortField : undefined;
	const sortDirectionNamed = searchParams.has('dir') ? view.sortDirection : undefined;
	const viewShare = useItemsViewShare({
		collection,
		collectionId: resolvedId,
		query: view.query,
		sortField: sortFieldNamed,
		sortDirection: sortDirectionNamed,
		groupBy: groupBy === DEFAULT_GROUP_TOKEN ? undefined : groupBy,
		review,
		// The merged list is ON by default; a view that turned it off must
		// share as off (utils/linkedRecords resolveLinkedParam reads the same
		// two spellings).
		linkedOff: ['0', 'false'].includes(view.linkedParam),
		// The card-tile filter lives outside `q` and no share link carries it;
		// the temp-link section owns saying so.
		cardFiltered: Boolean(view.cardFilter),
		// A board (KPR-1395) shares as the path it is, with its elements.
		board: resolveItemsViewMode(searchParams, resolvedId) === 'board'
			? { by: searchParams.get('board') || null, lanes: searchParams.get('lanes') || null }
			: null,
	});
	const disabled = !resolvedId;
	// Rebuilt when the collection's short code arrives (hooks/useRecordCodes).
	const codesVersion = useRecordCodesVersion();
	const shareUrl = useMemo(() => buildCollectionShareUrl(resolvedId), [resolvedId, codesVersion]); // eslint-disable-line react-hooks/exhaustive-deps
	const previewCard = useMemo(() => ({
		icon: collection?.icon,
		color: collection?.color,
		name: collection?.name,
	}), [collection?.icon, collection?.color, collection?.name]);
	const previewTitle = collection?.name || 'Untitled collection';
	const downloadName = useMemo(() => {
		const slug = slugify(previewTitle);
		const suffix = resolvedId ? `-${resolvedId}` : '';
		return `keepr-collection${slug ? `-${slug}` : ''}${suffix}`;
	}, [previewTitle, resolvedId]);

	// "Export view…" snapshots the SAME URL state share-view does: the items
	// page keeps filter (`card`, `q`, `review`) and sort (`sort`, `dir`) in the
	// URL, and the export endpoint takes the exact params the items feed sends
	// to GET /api/items (useCollectionItems), so the file matches the screen —
	// the review queue exports as the queue (keepr-api exportContext.js).
	const linkedSources = useLinkedSources(resolvedId, { enabled: Boolean(onItemsRoute) && canSeeLinks(collection) });
	const exportTarget = useMemo(() => {
		const params = { collection_id: resolvedId };
		if (view.cardFilter) params.card_id = view.cardFilter;
		if (view.query) params.q = view.query;
		if (review) params.review = review;
		if (sortFieldNamed) params.sort_field = sortFieldNamed;
		if (sortDirectionNamed) params.sort_direction = sortDirectionNamed;
		// "Then by" (KPR-1636): the file is in the list's order, levels included.
		Object.assign(params, sortThenParams(view.sortThen));
		// The merged list exports as merged: linked rows whose link allows
		// copies come along, the rest are counted and left out (the modal
		// says how many).
		if (resolveLinkedParam(view.linkedParam, linkedSources.hasIncoming)) params.includeLinked = 1;
		return { kind: 'items', params };
	}, [resolvedId, view, review, sortFieldNamed, sortDirectionNamed, linkedSources.hasIncoming]);

	// Excel asks "one line per entry" only when a card the export can hold
	// has a repeating group (KPR-685): the list's card when it names one,
	// else every card its list can hold (sub-collections' too).
	const cardDefinitions = useOptionalCardDefinitions();
	const exportOffersGroupShape = useMemo(() => {
		const cardsById = cardDefinitions?.cardsById || {};
		const named = exportTarget.params?.card_id;
		const all = Object.values(cardsById);
		const memberIds = named ? new Set([normalizeId(named)]) : getCollectionFamilyCardIds(collection, all);
		return cardsHoldRepeatingGroup(all.filter(card => memberIds.has(normalizeId(card?._id))), cardDefinitions?.getElementsForCard);
	}, [cardDefinitions, collection, exportTarget]);

	// "Save this export…": the list as it stands — card, filter, sort — becomes
	// a stored export's subject, and the format and options on screen its own.
	// Managers only: the Exports tab it opens is manage-gated, so offering it
	// to a reader would be a button that leads to a 403.
	const saveViewAsExport = ({ format, options }) => {
		// The collection is the definition's own, never a subject param. Nor
		// is `review`: a stored export never carries an unreviewed submission
		// (the runner drops them whatever the params say), so a queue saved
		// as a schedule would be a promise the run cannot keep.
		const params = { ...(exportTarget.params || {}) };
		delete params.collection_id;
		delete params.review;
		// A stored export runs as the collection's own list.
		delete params.includeLinked;
		setExportOpen(false);
		navigate(`/collections/${resolvedId}/form?tab=exports`, {
			state: {
				exportPrefill: {
					name: `${collection?.name || 'Collection'} items`.slice(0, 100),
					subject: { kind: 'items', params },
					format,
					options,
				},
			},
		});
	};

	// The grid/table mode the user is looking at: the ?view= param once the
	// switch has been touched, the stored per-collection preference otherwise
	// — so a "List (current view)" PDF is laid out like the screen. A map
	// (KPR-392) has no paper form; its list prints as the grid, the view it
	// came from.
	const viewNow = resolveItemsViewMode(searchParams, resolvedId);
	// A board (KPR-1395) prints as the grid too: its columns are a screen's.
	const itemsViewMode = viewNow === 'map' || viewNow === 'board' ? 'grid' : viewNow;

	const classes = ['collection-actions', className].filter(Boolean).join(' ').trim();

	// Honest rendering (the server re-checks everything): creating items needs
	// write somewhere in the collection. While the collection record is still
	// loading (null), the buttons render DISABLED rather than hidden — no header layout shift on navigation, and
	// gating applies the moment access is known.
	const accessKnown = Boolean(collection);
	const showNewItem = showPrimary && (!accessKnown || canCreateItems(collection));
	const accessPending = !accessKnown;

	const helpAction = useHelpAction();
	// "Pin to Home" (KPR-1125): the collection as a shortcut among the person's
	// everyday ones. Its own pin, never the star above it.
	const favorites = useOptionalFavorites();

	// A GLOBAL collection (plan §4.7): a consumer may follow it (listed under
	// Global on the Collections page) and link to it, and export it only when
	// the owner allows; nobody deletes one while it is published or retiring.
	const global = accessKnown && isGlobal(collection);
	const consumer = accessKnown && isGlobalConsumer(collection);
	const followed = collection?.followed === true;
	const mayFollow = global && !isOwner(collection) && collection?.publication?.state === 'published'
		&& collection?.publication?.allowFollow !== false;
	const exportAllowed = !consumer || collection?.publication?.allowExport !== false;
	const toggleFollow = async () => {
		setFollowing(true);
		try {
			if (followed) await unfollowCollection(resolvedId);
			else await followCollection(resolvedId);
			message.success(followed ? `You no longer follow ${collection?.name || 'this collection'}.` : `You follow ${collection?.name || 'this collection'}. It is listed under Global on your Collections page.`);
			if (typeof refreshCollection === 'function') refreshCollection();
		} catch (e) {
			message.error(publicationRefusal(e));
		} finally {
			setFollowing(false);
		}
	};

	// Deleting a collection is a typed-confirmation flow in the Danger zone;
	// the menu entry is the door to it, for its managers like the tab itself
	// (KPR-1267: a collection's managers may do what its owner may, except
	// transfer it).
	// The page's own entries (Settings' Export…), handed up through the shell slot.
	const pageMenuActions = useContext(ShellSlotContext)?.menuActions;
	const overflowMenu = buildActionMenu([
		...(Array.isArray(pageMenuActions) ? pageMenuActions : []),
		(mayFollow || (global && followed)) ? {
			key: 'follow',
			group: 'record',
			icon: <BOOKMARK />,
			label: followed ? 'Unfollow' : 'Follow',
			disabled: following,
			onClick: toggleFollow,
		} : null,
		typeof onToggleFavorite === 'function' ? {
			key: 'favorite',
			group: 'record',
			icon: isFavorite ? <RATING_STAR_FILL /> : <RATING_STAR_OUTLINE />,
			label: isFavorite ? 'Remove from favorites' : 'Add to favorites',
			onClick: onToggleFavorite,
		} : null,
		// A collection or sub-collection as a Home shortcut (KPR-1125). Not a
		// global consumer's (the API's meta read refuses them), like the star.
		accessKnown && canPinCollection(collection) && resolvedId && typeof favorites?.togglePin === 'function' ? {
			key: 'pin-home',
			group: 'record',
			glyph: 'pin',
			label: favorites.isPinned?.(PIN_KIND.COLLECTION_LINK, resolvedId) ? 'Unpin from Home' : 'Pin to Home',
			onClick: () => favorites.togglePin(PIN_KIND.COLLECTION_LINK, { targetId: resolvedId }),
		} : null,
		// Tag… — my PRIVATE tags on the collection (ruled b53): read access of
		// my own is enough (not a global consumer's, not a link-only
		// reader's — the private store's rule), and only I see them.
		accessKnown && canTagCollectionPrivately(collection) ? {
			key: 'tag',
			group: 'record',
			glyph: 'tag',
			label: 'Tag…',
			disabled,
			onClick: () => setTagOpen(true),
		} : null,
		accessKnown && canAddSubCollection(collection) ? {
			key: 'new-sub-collection',
			group: 'record',
			icon: <PLUS />,
			label: 'New sub-collection…',
			onClick: () => setNewSubOpen(true),
		} : null,
		// Attach files… (KPR-187): many files onto the items listed, each
		// matched to its item. The items page owns the panel; this menu and that
		// page share no channel, so it asks through the URL (?attach=1), which
		// the page reads, acts on and drops. Only where the collection takes
		// files and the person can write items in it now.
		accessKnown && onItemsRoute && collection?.allowAttachments === true && canAddItemsNow(collection) ? {
			key: 'attach-files',
			group: 'record',
			glyph: 'attach',
			label: 'Attach files…',
			onClick: () => {
				const next = new URLSearchParams(searchParams);
				next.set('attach', '1');
				navigate(`${location.pathname}?${next.toString()}`, { replace: true });
			},
		} : null,
		// Import a spreadsheet (KPR-1899): for anyone who can add items now, on
		// every collection page. The page chooses among the cards they can write.
		accessKnown && canAddItemsNow(collection) && resolvedId ? {
			key: 'import-spreadsheet',
			group: 'record',
			icon: <UPLOAD />,
			label: `${IMPORT_LABEL}…`,
			onClick: () => navigate(importPath(resolvedId)),
		} : null,
		...(accessKnown && canManage(collection) && collection?.status !== 'archived' ? exampleData.map(entry => ({
			key: `add-example-data-${entry.key}`,
			group: 'record',
			icon: <PLUS />,
			label: addExampleDataLabel(entry, exampleData.length > 1),
			disabled,
			onClick: () => confirmAddExamples(entry),
		})) : []),
		accessKnown && canAttachSubCollection(collection) ? {
			key: 'attach-sub-collection',
			group: 'record',
			glyph: 'collection',
			label: 'Attach an existing collection…',
			onClick: () => setAttachOpen(true),
		} : null,
		{
			key: 'share',
			group: 'share',
			glyph: 'share',
			label: 'Share collection',
			disabled: disabled || !shareUrl,
			onClick: () => setShareOpen(true),
		},
		onItemsRoute ? {
			key: 'share-view',
			group: 'share',
			glyph: 'share',
			label: 'Share this view',
			disabled,
			onClick: () => viewShare.share(),
		} : null,
		onItemsRoute && exportAllowed ? {
			key: 'export-view',
			group: 'share',
			glyph: 'export',
			label: 'Export view…',
			disabled,
			onClick: () => setExportOpen(true),
		} : null,
		// Export everything (KPR-742): every item and file the person can read,
		// as one zip, from anywhere in the collection. It opens the settings
		// section that starts it and lists what was made.
		accessKnown && exportAllowed && resolvedId ? {
			key: 'export-everything',
			group: 'share',
			glyph: 'export',
			label: 'Export everything…',
			disabled,
			onClick: () => navigate(`/collections/${resolvedId}/form?tab=export-everything`),
		} : null,
		// A personal collection has no Danger zone (it leaves only with its
		// account), so no door to one.
		accessKnown && canManage(collection) && !isPersonal(collection) && !global ? {
			key: 'delete',
			group: 'destructive',
			glyph: 'delete',
			label: 'Delete collection…',
			onClick: () => navigate(`/collections/${resolvedId}/form?tab=danger`),
		} : null,
		// Remove demo data (KPR-343): the example records a Discover package
		// brought. Present only while at least one is marked (the API counts
		// them for managers alone, `demoItems`), and never on an archived
		// collection, which is read-only.
		accessKnown && canManage(collection) && collection?.status !== 'archived' && collection?.demoItems > 0 ? {
			key: 'remove-demo-data',
			group: 'destructive',
			glyph: 'delete',
			label: `Remove demo data (${collection.demoItems})…`,
			// The dialog asks the API what the removal would do and says it,
			// including which of the person's own records lose a link (KPR-363).
			onClick: () => setDemoOpen(true),
		} : null,
		// The collection's bar draws its own ⋯ (it sits before the primary
		// button, not after it), so it adds the Help entry ContextBar would
		// otherwise have added for it.
		helpAction,
	]);

	return (
		<>
			<div className={classes}>
				<Dropdown trigger={['click']} placement="bottomRight" menu={overflowMenu || { items: [] }}>
					<Button
						icon={<KEBAB />}
						aria-label="More actions"
						data-shell-menu
						disabled={disabled || !overflowMenu}
					/>
				</Dropdown>
				{showNewItem ? (
					<AddItemButton
						collectionId={resolvedId}
						collection={collection}
						buttonProps={{
							type: 'primary',
							icon: <PLUS />,
							// Hidden on phones — the items page's FAB is the
							// mobile primary (the modal stays reachable via
							// the FAB's programmatic click).
							className: 'context-bar__primary',
							'data-shell-primary': true,
							disabled: disabled || accessPending,
						}}
					/>
				) : null}
			</div>

			{viewShare.modal}

			{demoOpen ? (
				<RemoveDemoDataModal
					open={demoOpen}
					collectionId={resolvedId}
					onClose={() => setDemoOpen(false)}
					onRemoved={() => { if (typeof refreshCollection === 'function') refreshCollection(); }}
				/>
			) : null}
			{newSubOpen ? (
				<NewSubCollectionModal open={newSubOpen} onClose={() => setNewSubOpen(false)} parent={collection} />
			) : null}
			{tagOpen ? (
				<CollectionTagsModal open={tagOpen} onClose={() => setTagOpen(false)} collection={collection} collectionId={resolvedId} />
			) : null}
			{attachOpen ? (
				<AttachSubCollectionModal
					open={attachOpen}
					onClose={() => setAttachOpen(false)}
					parent={collection}
					onAttached={() => { if (typeof refreshCollection === 'function') refreshCollection(); }}
				/>
			) : null}

			<ExportModal
				open={exportOpen}
				onClose={() => setExportOpen(false)}
				target={exportTarget}
				title='Export view'
				baseName={`${collection?.name || 'collection'}-items`}
				supportsExcel
				listLayout={itemsViewMode}
				onSaveAsScheduled={accessKnown && canManage(collection) && resolvedId ? saveViewAsExport : null}
				offersGroupShape={exportOffersGroupShape}
			/>

			<ShareModal
				open={shareOpen}
				onClose={() => setShareOpen(false)}
				modalTitle="Share collection"
				preview={(
					<RecordIdentity
						kicker="Collection"
						badge={<RecordBadge kind="collection" record={previewCard} title={previewTitle} />}
					/>
				)}
				shareUrl={shareUrl}
				downloadFileName={downloadName}
				extra={accessKnown && !canManage(collection) ? null : (
					// The whole collection as one public link: a view link with
					// no query. There is no separate "collection" kind — an
					// unfiltered view IS the collection. A manager's surface: a
					// reader (a global collection's consumer) shares the direct
					// link only.
					<TempShareLinkSection
						collectionId={resolvedId}
						collection={collection}
						kind="view"
					/>
				)}
			/>
		</>
	);
}
